import * as bootstrap from 'bootstrap';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { handleClear } from './anonymization';
import { appState } from './appState';
import { sendByokMessage, sendMockMessage } from './byok';
import { openDeleteModal } from './deleteModal';
import { dom } from './dom';
import { deanonymizeWithSnapshot, ensureProjectEntityContext, resolveEntityMapSnapshot } from './entityMaps';
import { getIconPath } from './files';
import { escapeHtml } from './htmlEscape';
import { t } from './i18n';
import { buildModelContext, queueChatForMemory, queueMemoryForMessage } from './memory';
import { refreshProjectSettingsContext } from './settings';
import { storage } from './storage';
import { store } from './store';
export function resetProjectSelection() {
    const projects = store.getProjects();
    if (projects.length === 0) {
        appState.currentProjectId = null;
        appState.currentChatId = null;
        return;
    }
    if (!appState.currentProjectId || !store.getProject(appState.currentProjectId)) {
        appState.currentProjectId = projects[0].id;
    }
    const project = appState.currentProjectId ? store.getProject(appState.currentProjectId) : undefined;
    if (!project) {
        appState.currentChatId = null;
        return;
    }
    if (!appState.currentChatId || !project.chats.find(chat => chat.id === appState.currentChatId)) {
        appState.currentChatId = project.chats.length > 0 ? project.chats[0].id : null;
    }
}
export function renderProjects() {
    const projects = store.getProjects();
    if (projects.length > 0 && !appState.currentProjectId)
        appState.currentProjectId = projects[0].id;
    renderProjectList(dom.projectList, projects, false);
    renderProjectList(dom.mobileProjectList, projects, true);
}
export function renderChats() {
    const project = appState.currentProjectId ? store.getProject(appState.currentProjectId) ?? null : null;
    renderChatList(dom.chatList, project, false);
    renderChatList(dom.mobileChatList, project, true);
}
export async function loadChat() {
    dom.chatHistory.innerHTML = '';
    // Allow loading chat without login (anonymous mode)
    if (!appState.currentProjectId || !appState.currentChatId) {
        dom.chatHistory.innerHTML = `<div class="text-center py-5 text-muted"><p>${t('chat.selectChat')}</p></div>`;
        return;
    }
    const projectId = appState.currentProjectId;
    const chatId = appState.currentChatId;
    if (!projectId || !chatId) {
        return;
    }
    await ensureProjectEntityContext(projectId);
    appState.currentEntityMaps = null;
    await store.loadChatMessages(projectId, chatId);
    const chat = store.getChat(projectId, chatId);
    if (chat && chat.messages.length > 0) {
        chat.messages.forEach(appendMessage);
        queueChatForMemory(projectId, chatId, chat.messages);
    }
    else {
        dom.chatHistory.innerHTML = `<div class="text-center py-5 text-muted"><p>${t('chat.noMessages')}</p></div>`;
    }
}
async function confirmSendResponsibility() {
    const user = store.getUser();
    const userId = user?.id ?? 'anonymous';
    const storageKey = `blindquery_send_confirm_${userId}`;
    const storedPreference = await storage.load(storageKey);
    if (storedPreference) {
        return true;
    }
    const modalElement = document.getElementById('sendConfirmModal');
    const checkbox = document.getElementById('sendConfirmCheckbox');
    const confirmButton = document.getElementById('sendConfirmProceed');
    const cancelButton = document.getElementById('sendConfirmCancel');
    if (!modalElement || !confirmButton || !cancelButton) {
        return window.confirm(t('chat.confirmSend'));
    }
    const modal = new bootstrap.Modal(modalElement);
    if (checkbox) {
        checkbox.checked = false;
    }
    return new Promise(resolve => {
        let resolved = false;
        const finish = (value) => {
            if (resolved)
                return;
            resolved = true;
            confirmButton.removeEventListener('click', onConfirm);
            cancelButton.removeEventListener('click', onCancel);
            modalElement.removeEventListener('hidden.bs.modal', onHidden);
            resolve(value);
        };
        const onConfirm = () => {
            if (checkbox) {
                checkbox.checked = true;
            }
            void storage.save(storageKey, true);
            modal.hide();
            finish(true);
        };
        const onCancel = () => {
            modal.hide();
            finish(false);
        };
        const onHidden = () => {
            finish(false);
        };
        confirmButton.addEventListener('click', onConfirm);
        cancelButton.addEventListener('click', onCancel);
        modalElement.addEventListener('hidden.bs.modal', onHidden);
        modal.show();
    });
}
export async function sendMessageToAI() {
    const anonymizedText = dom.currentAnonymizedText.value;
    const modelId = dom.modelSelector.value;
    if (!appState.currentProjectId) {
        const project = store.addProject(t('chat.defaultProject'));
        appState.currentProjectId = project.id;
    }
    if (!appState.currentChatId && appState.currentProjectId) {
        const chat = store.addChat(appState.currentProjectId, t('chat.defaultChat', { time: new Date().toLocaleTimeString() }));
        appState.currentChatId = chat?.id || null;
    }
    resetProjectSelection();
    renderProjects();
    renderChats();
    await loadChat();
    await refreshProjectSettingsContext();
    if (!appState.currentProjectId || !appState.currentChatId) {
        alert(t('alerts.defaultChatError'));
        return;
    }
    const projectId = appState.currentProjectId;
    const chatId = appState.currentChatId;
    if (!projectId || !chatId) {
        alert(t('alerts.resolveChatError'));
        return;
    }
    const shouldSend = await confirmSendResponsibility();
    if (!shouldSend) {
        return;
    }
    setLoading(true);
    try {
        const entityMapsSnapshot = resolveEntityMapSnapshot();
        const userMsg = {
            id: crypto.randomUUID(),
            role: 'user',
            content: dom.inputText.value,
            anonymizedContent: anonymizedText,
            entityMaps: entityMapsSnapshot || undefined,
            timestamp: new Date(),
            files: appState.uploadedFiles.map(file => ({ name: file.name, icon: file.icon }))
        };
        store.addMessage(projectId, chatId, userMsg);
        queueMemoryForMessage(projectId, chatId, userMsg);
        appendMessage(userMsg);
        handleClear();
        const loadingId = crypto.randomUUID();
        const loadingDiv = document.createElement('div');
        loadingDiv.id = `loading-${loadingId}`;
        loadingDiv.className = 'd-flex justify-content-start mb-3';
        loadingDiv.innerHTML = `
      <div class="message-bubble shadow-sm p-3 message-ai" style="min-width: 100px;">
        <div class="d-flex align-items-center gap-2">
          <div class="spinner-grow spinner-grow-sm text-primary" role="status" style="width: 0.5rem; height: 0.5rem; animation-duration: 1s;"></div>
          <div class="spinner-grow spinner-grow-sm text-primary" role="status" style="width: 0.5rem; height: 0.5rem; animation-duration: 1s; animation-delay: 0.2s;"></div>
          <div class="spinner-grow spinner-grow-sm text-primary" role="status" style="width: 0.5rem; height: 0.5rem; animation-duration: 1s; animation-delay: 0.4s;"></div>
        </div>
      </div>
    `;
        dom.chatHistory.appendChild(loadingDiv);
        const chatWindow = document.getElementById('chatWindow');
        if (chatWindow) {
            chatWindow.scrollTop = chatWindow.scrollHeight;
        }
        const modelOption = appState.availableModels.find(model => model.id === modelId);
        if (!modelOption) {
            throw new Error(t('errors.modelUnavailable'));
        }
        const modelContext = await buildModelContext(projectId, chatId, userMsg, appState.uploadedFiles);
        const response = modelOption.type === 'byok'
            ? await sendByokMessage(modelContext, modelOption)
            : await sendMockMessage(modelContext, modelOption.id);
        const loadingElement = document.getElementById(`loading-${loadingId}`);
        if (loadingElement)
            loadingElement.remove();
        const aiMsg = {
            id: crypto.randomUUID(),
            role: 'ai',
            content: response,
            entityMaps: entityMapsSnapshot || undefined,
            timestamp: new Date()
        };
        store.addMessage(projectId, chatId, aiMsg);
        queueMemoryForMessage(projectId, chatId, aiMsg);
        appendMessage(aiMsg);
        if (chatWindow) {
            chatWindow.scrollTop = chatWindow.scrollHeight;
        }
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.unknown');
        alert(t('alerts.sendFailure', { error: errorMessage }));
    }
    finally {
        setLoading(false);
    }
}
export function appendMessage(msg) {
    const messageDiv = document.createElement('div');
    messageDiv.className = `d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-3`;
    const bubble = document.createElement('div');
    bubble.className = `message-bubble shadow-sm p-0 overflow-hidden ${msg.role === 'user' ? 'message-user' : 'message-ai'}`;
    bubble.style.minWidth = msg.role === 'ai' ? '300px' : 'auto';
    if (msg.role === 'ai') {
        const unanonymizedText = msg.entityMaps
            ? deanonymizeWithSnapshot(msg.content, msg.entityMaps)
            : appState.entityContext ? appState.entityContext.deanonymize(msg.content) : msg.content;
        const msgId = msg.id.replace(/-/g, '');
        const isDemoDefaultPure = appState.demoActive;
        const unanonActiveClass = isDemoDefaultPure ? '' : 'active';
        const unanonActivePaneClass = isDemoDefaultPure ? '' : 'show active';
        const unanonSelected = isDemoDefaultPure ? 'false' : 'true';
        const pureActiveClass = isDemoDefaultPure ? 'active' : '';
        const pureActivePaneClass = isDemoDefaultPure ? 'show active' : '';
        const pureSelected = isDemoDefaultPure ? 'true' : 'false';
        bubble.innerHTML = `
      <div class="card border-0 bg-transparent">
        <div class="card-header bg-transparent pt-2 pb-0 border-bottom-0">
          <div class="d-flex justify-content-between align-items-center">
            <ul class="nav nav-tabs card-header-tabs border-bottom-0" id="tabs-${msgId}" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link ${unanonActiveClass} py-1 px-3 small border-0" id="unanon-tab-${msgId}" data-bs-toggle="tab" data-bs-target="#unanon-${msgId}" type="button" role="tab" aria-selected="${unanonSelected}" style="font-size: 0.75rem;">
                  ${t('chat.tabUnanonymized')}
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link ${pureActiveClass} py-1 px-3 small border-0" id="pure-tab-${msgId}" data-bs-toggle="tab" data-bs-target="#pure-${msgId}" type="button" role="tab" aria-selected="${pureSelected}" style="font-size: 0.75rem;">
                  ${t('chat.tabPureResponse')}
                </button>
              </li>
            </ul>
            <button class="btn btn-link btn-sm p-0 text-muted opacity-50 me-2" id="copy-btn-${msgId}" title="${t('chat.copyContentTitle')}">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-copy" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6ZM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a.5.5 0 0 1 1 0v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1a.5.5 0 0 1 0 1H2Z"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="card-body p-3">
          <div class="tab-content border-0 p-0 bg-transparent min-auto" id="content-${msgId}">
            <div class="tab-pane fade ${unanonActivePaneClass}" id="unanon-${msgId}" role="tabpanel" aria-labelledby="unanon-tab-${msgId}">
              ${DOMPurify.sanitize(marked.parse(unanonymizedText))}
            </div>
            <div class="tab-pane fade ${pureActivePaneClass}" id="pure-${msgId}" role="tabpanel" aria-labelledby="pure-tab-${msgId}">
              ${DOMPurify.sanitize(marked.parse(msg.content))}
            </div>
          </div>
        </div>
      </div>
    `;
        setTimeout(() => {
            const copyBtn = bubble.querySelector(`#copy-btn-${msgId}`);
            if (copyBtn) {
                copyBtn.addEventListener('click', () => {
                    const activePane = bubble.querySelector('.tab-pane.active');
                    const textToCopy = activePane.innerText;
                    navigator.clipboard.writeText(textToCopy).then(() => {
                        const originalHtml = copyBtn.innerHTML;
                        copyBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-check2 text-success" viewBox="0 0 16 16"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>';
                        setTimeout(() => {
                            copyBtn.innerHTML = originalHtml;
                        }, 2000);
                    });
                });
            }
        }, 0);
    }
    else {
        const msgId = msg.id.replace(/-/g, '');
        const anonymizedText = msg.anonymizedContent?.trim() ? msg.anonymizedContent : msg.content;
        let attachmentsHtml = '';
        if (msg.files && msg.files.length > 0) {
            attachmentsHtml += '<div class="d-flex flex-wrap gap-2 mb-2">';
            msg.files.forEach(file => {
                attachmentsHtml += `
          <div class="d-flex align-items-center bg-white bg-opacity-10 border border-white border-opacity-25 rounded py-1 px-2 small">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-${file.icon} me-2" viewBox="0 0 16 16">
              ${getIconPath(file.icon)}
            </svg>
            <span class="text-truncate" style="max-width: 120px;">${escapeHtml(file.name)}</span>
          </div>
        `;
            });
            attachmentsHtml += '</div>';
        }
        bubble.innerHTML = `
      <div class="card border-0 bg-transparent">
        <div class="card-header bg-transparent pt-2 pb-0 border-bottom-0">
          <div class="d-flex justify-content-between align-items-center">
            <ul class="nav nav-tabs card-header-tabs border-bottom-0" id="tabs-${msgId}" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link active py-1 px-3 small border-0" id="orig-tab-${msgId}" data-bs-toggle="tab" data-bs-target="#orig-${msgId}" type="button" role="tab" aria-selected="true" style="font-size: 0.75rem;">
                  ${t('chat.tabOriginal')}
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button class="nav-link py-1 px-3 small border-0" id="anon-tab-${msgId}" data-bs-toggle="tab" data-bs-target="#anon-${msgId}" type="button" role="tab" aria-selected="false" style="font-size: 0.75rem;">
                  ${t('chat.tabAnonymized')}
                </button>
              </li>
            </ul>
            <button class="btn btn-link btn-sm p-0 text-muted opacity-50 me-2" id="copy-btn-${msgId}" title="${t('chat.copyContentTitle')}">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-copy" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H6ZM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a.5.5 0 0 1 1 0v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1a.5.5 0 0 1 0 1H2Z"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="card-body p-3">
          ${attachmentsHtml}
          <div class="tab-content border-0 p-0 bg-transparent min-auto" id="content-${msgId}">
            <div class="tab-pane fade show active" id="orig-${msgId}" role="tabpanel" aria-labelledby="orig-tab-${msgId}">
              ${DOMPurify.sanitize(marked.parse(msg.content || ''))}
            </div>
            <div class="tab-pane fade" id="anon-${msgId}" role="tabpanel" aria-labelledby="anon-tab-${msgId}">
              ${DOMPurify.sanitize(marked.parse(anonymizedText || ''))}
            </div>
          </div>
        </div>
      </div>
    `;
        setTimeout(() => {
            const copyBtn = bubble.querySelector(`#copy-btn-${msgId}`);
            if (copyBtn) {
                copyBtn.addEventListener('click', () => {
                    const activePane = bubble.querySelector('.tab-pane.active');
                    const textToCopy = activePane.innerText;
                    navigator.clipboard.writeText(textToCopy).then(() => {
                        const originalHtml = copyBtn.innerHTML;
                        copyBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="bi bi-check2 text-success" viewBox="0 0 16 16"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>';
                        setTimeout(() => {
                            copyBtn.innerHTML = originalHtml;
                        }, 2000);
                    });
                });
            }
        }, 0);
    }
    messageDiv.appendChild(bubble);
    dom.chatHistory.appendChild(messageDiv);
    const chatWindow = document.getElementById('chatWindow');
    if (chatWindow) {
        chatWindow.scrollTop = chatWindow.scrollHeight;
    }
}
export function closeSidebarOffcanvas() {
    const offcanvasElement = document.getElementById('sidebarOffcanvas');
    if (!offcanvasElement)
        return;
    const offcanvas = bootstrap.Offcanvas.getInstance(offcanvasElement);
    offcanvas?.hide();
}
function renderProjectList(targetList, projects, closeOnSelect) {
    if (!targetList)
        return;
    targetList.innerHTML = '';
    projects.forEach(project => {
        const row = document.createElement('div');
        row.className = `list-group-item list-group-item-action border-0 py-2 px-3 small d-flex align-items-center gap-2 ${project.id === appState.currentProjectId ? 'active' : ''}`;
        row.innerHTML = `
      <span class="flex-grow-1 text-truncate">${escapeHtml(project.name)}</span>
      <button class="btn btn-sm btn-link text-danger p-0" title="${escapeHtml(t('chat.deleteProjectTitle'))}">
        <i class="bi bi-trash"></i>
      </button>
    `;
        row.addEventListener('click', () => {
            appState.currentProjectId = project.id;
            const nextProject = store.getProject(project.id);
            appState.currentChatId = (nextProject && nextProject.chats.length > 0) ? nextProject.chats[0].id : null;
            renderProjects();
            renderChats();
            void loadChat();
            void refreshProjectSettingsContext();
            if (closeOnSelect)
                closeSidebarOffcanvas();
        });
        const deleteBtn = row.querySelector('button');
        deleteBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            openDeleteModal('project', project.id, null, project.name);
        });
        targetList.appendChild(row);
    });
}
function renderChatList(targetList, project, closeOnSelect) {
    if (!targetList)
        return;
    targetList.innerHTML = '';
    if (!project)
        return;
    project.chats.forEach(chat => {
        const row = document.createElement('div');
        row.className = `list-group-item list-group-item-action border-0 py-1 px-3 small rounded-pill mb-1 d-flex align-items-center gap-2 ${chat.id === appState.currentChatId ? 'active' : ''}`;
        row.innerHTML = `
      <span class="flex-grow-1 text-truncate">${escapeHtml(chat.name)}</span>
      <button class="btn btn-sm btn-link text-danger p-0" title="${escapeHtml(t('chat.deleteChatTitle'))}">
        <i class="bi bi-trash"></i>
      </button>
    `;
        row.addEventListener('click', () => {
            appState.currentChatId = chat.id;
            renderChats();
            void loadChat();
            if (closeOnSelect)
                closeSidebarOffcanvas();
        });
        const deleteBtn = row.querySelector('button');
        deleteBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            openDeleteModal('chat', project.id, chat.id, chat.name);
        });
        targetList.appendChild(row);
    });
}
function setLoading(show) {
    dom.mainActionBtn.disabled = show;
    dom.btnSpinner.classList.toggle('d-none', !show);
}
